import React, { useState, useEffect }  from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from '../../../../assets/locale/messages';
import RadioButtonsGroup from '../../../../components/RadioButton'
import Modal from '../../../../components/Modal'
import { updateBookingRequest, getRequestStatus } from '../../../../store/Bookings/actions'

const BookingCancelationReasonForm = ({open, setOpen}) => {

    const dispatch = useDispatch();
    const lang = useSelector((state) => state.locale.lang);
    const { bookings, shared} = messages[lang];
    const bookingCancelationReasons = useSelector((state) => state.bookings.bookingCancelationReasons);
    const bookingData = useSelector((state) => state.bookings.bookingDetails);
    const [bookingCancelationReasonsList, setBookingCancelationReasonsList] = useState([]);
    const [selectedReasonObject, setSelectedReasonObject] = useState(null);
    const [showRefundChoicePopup, setShowRefundChoicePopup] = useState(false);
    const [showCustomerNoShowPopup, setShowCustomerNoShowPopup] = useState(false);
    const [pendingReasonId, setPendingReasonId] = useState(null);

    const isEmployeeNoShow = (reason) => reason?.key === 'employee_did_not_show_up';
    const isCustomerNoShow = (reason) => reason?.key === 'customer_did_not_show_up';

    const dispatchCancelBooking = (reasonId, applyRefund) => {
        const params = { appointment_cancellation_reason_id: reasonId };
        if (applyRefund !== undefined) params.apply_refund = applyRefund;
        dispatch(updateBookingRequest({
            data: {
                id: bookingData?.id,
                params
            },
            requestType: "cancel"
        }));
        dispatch(getRequestStatus(false));
    };

    const {
        handleSubmit,
        setFieldTouched,
        setFieldValue,
        touched,
        errors,
        resetForm,
        } = useFormik({
            initialValues:{
                cancelationReason: ""
            },
            validationSchema: Yup.object({
                cancelationReason: Yup.string().required('Required')
            }),
            onSubmit: ({ cancelationReason }) => {
                const reasonId = +cancelationReason;
                setPendingReasonId(reasonId);
                closeForm();

                if (isEmployeeNoShow(selectedReasonObject)) {
                    dispatchCancelBooking(reasonId, true);
                } else if (isCustomerNoShow(selectedReasonObject)) {
                    setShowCustomerNoShowPopup(true);
                } else {
                    setShowRefundChoicePopup(true);
                }
            }
     });

    const closeForm = () => {
        setOpen(false);
        setSelectedReasonObject(null);
        resetForm();
    };

    useEffect(()=>{
        const reasons = (bookingCancelationReasons || []).map(reason => ({
            id: reason.id,
            label: <div className="fsize-20 fweight-400 text-gray">{reason.reason}</div>,
            value: String(reason.id)
        }))
        setBookingCancelationReasonsList([...reasons]);
    },[lang, bookingCancelationReasons]);

    const handleCloseCancelationReasonPopup = () => {
        closeForm();
    };

    const renderCancelationReasonPopupContent = () => (
        <div>
            <div className="fsize-20 fweight-400 mb-3">{bookings.details.bookingCancelationReasonLabel}*</div>
            <RadioButtonsGroup
                name="cancelationReason"
                labelId="cancelationReason"
                value={selectedReasonObject ? String(selectedReasonObject.id) : ""}
                list={bookingCancelationReasonsList}
                onChange={(e) => {
                    setFieldTouched("cancelationReason");
                    setFieldValue("cancelationReason", e.target.value);
                    const reason = bookingCancelationReasons?.find(r => String(r.id) === e.target.value);
                    setSelectedReasonObject(reason || null);
                  }}
            />
            {isEmployeeNoShow(selectedReasonObject) && (
                <div className="fsize-16 fweight-400 text-success mt-2">
                    {bookings.details.employeeNoShowRefundInfo}
                </div>
            )}
        </div>
    );

    return(
        <>
            <Modal
                open={open}
                handleClose={handleCloseCancelationReasonPopup}
                title={<span className="fsize-26 fweight-400">{bookings.details.cancelBooking}</span>}
                contentClass="mb-3 pb-2 text-primary"
                content={renderCancelationReasonPopupContent()}
                confirmBtnTxt={shared.submit}
                cancelBtnTxt={shared.cancel}
                isContentText={false}
                handleConfirm={handleSubmit}
                handleCancel={handleCloseCancelationReasonPopup}
                maxWidth="sm"
                actionsClass=" px-4"
                confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
                cancelBtnClass="fsize-14 py-2 text-secondary px-5 "
                disableActionBtn={!!(!touched["cancelationReason"] || errors["cancelationReason"])}
            />

            <Modal
                open={showRefundChoicePopup}
                handleClose={() => {
                    setPendingReasonId(null);
                    setShowRefundChoicePopup(false);
                }}
                title={<span className="fsize-26 fweight-400">{bookings.details.cancelBooking}</span>}
                contentClass="mb-3 pb-2 text-primary"
                content={<span className="fsize-20 fweight-400">{bookings.details.refundChoicePopupContent}</span>}
                confirmBtnTxt={shared.yes}
                cancelBtnTxt={shared.no}
                isContentText={false}
                handleConfirm={() => {
                    dispatchCancelBooking(pendingReasonId, true);
                    setPendingReasonId(null);
                    setShowRefundChoicePopup(false);
                }}
                handleCancel={() => {
                    dispatchCancelBooking(pendingReasonId, false);
                    setPendingReasonId(null);
                    setShowRefundChoicePopup(false);
                }}
                maxWidth="sm"
                actionsClass=" px-4"
                confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
                cancelBtnClass="fsize-14 py-2 text-secondary px-5"
            />

            <Modal
                open={showCustomerNoShowPopup}
                handleClose={() => {
                    setPendingReasonId(null);
                    setShowCustomerNoShowPopup(false);
                }}
                title={<span className="fsize-26 fweight-400">{bookings.details.cancelBooking}</span>}
                contentClass="mb-3 pb-2 text-primary"
                content={<span className="fsize-20 fweight-400">{bookings.details.customerNoShowPopupContent}</span>}
                confirmBtnTxt={shared.ok}
                cancelBtnTxt={bookings.details.refundAnyways}
                isContentText={false}
                handleConfirm={() => {
                    dispatchCancelBooking(pendingReasonId, false);
                    setPendingReasonId(null);
                    setShowCustomerNoShowPopup(false);
                }}
                handleCancel={() => {
                    dispatchCancelBooking(pendingReasonId, true);
                    setPendingReasonId(null);
                    setShowCustomerNoShowPopup(false);
                }}
                maxWidth="sm"
                actionsClass=" px-4"
                confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
                cancelBtnClass="fsize-14 py-2 text-secondary px-5"
            />
        </>
    )
}

export default BookingCancelationReasonForm;
